/**
 * Chat (DESIGN 6.1 Enter, 6.6 "chat on the left"): a log of the last eight
 * lines that fades as it ages, and an input that opens on Enter (via the
 * Controls "chat" intent), sends on Enter, closes on Escape or blur. While
 * the input is open the game must not see keys: main.ts subscribes to
 * `onOpenChange` and calls `controls.setEnabled(false)`, and the input stops
 * key events from bubbling to the window so the closing Enter cannot
 * reopen the box through Controls in the same event.
 */

import { MAX_CHAT_LENGTH } from '@shared/index.js';
import type { ClientState } from '../state/clientState.js';
import type { Socket } from '../net/socket.js';
import type { ChatLine } from '../state/viewModel.js';
import { type Screen, clear, el } from './dom.js';
import { lastLines, sanitizeChat } from './helpers.js';
import { discoveries, isRevealCode } from '../state/discoveries.js';

export interface ChatScreen extends Screen {
  open(): void;
  close(): void;
  readonly isOpen: boolean;
  /** Called with true when the input opens and false when it closes. Returns an unsubscribe function. */
  onOpenChange(fn: (open: boolean) => void): () => void;
  /** Called when the cheat code has opened the whole book (it never reaches the room). */
  onReveal(fn: () => void): void;
}

/** Lines shown in the log (DESIGN 7.4 WP7 brief). */
export const CHAT_LINES = 8;
/** A line is fully visible for this long, then fades to CHAT_MIN_ALPHA over the next CHAT_FADE_MS. */
const CHAT_HOLD_MS = 8000;
const CHAT_FADE_MS = 8000;
const CHAT_MIN_ALPHA = 0.15;
const FADE_STEP_MS = 250;

/** Opacity for a line `age` ms old (1 while the input is open). */
export function lineAlpha(age: number, inputOpen: boolean): number {
  if (inputOpen || age <= CHAT_HOLD_MS) return 1;
  const k = Math.min(1, (age - CHAT_HOLD_MS) / CHAT_FADE_MS);
  return 1 - k * (1 - CHAT_MIN_ALPHA);
}

export function mount(root: HTMLElement, state: ClientState, socket: Socket): ChatScreen {
  const log = el('ul', { class: 'chat-log', 'aria-live': 'polite' });
  const input = el('input', {
    class: 'input chat-input', type: 'text', maxlength: MAX_CHAT_LENGTH, autocomplete: 'off', spellcheck: 'false',
    placeholder: 'Say something... (Enter sends, Esc closes)', 'aria-label': 'Chat message',
  });
  input.hidden = true;
  clear(root);
  root.classList.add('chat');
  root.append(log, input);

  let open = false;
  let suppressed = false;
  let lastChat: ChatLine[] | null = null;
  let shown: { node: HTMLElement; t: number }[] = [];
  let lastFadeAt = 0;
  const listeners = new Set<(o: boolean) => void>();
  let revealed: (() => void) | null = null;

  function emit(): void {
    for (const fn of listeners) fn(open);
  }

  function renderLog(lines: readonly ChatLine[]): void {
    clear(log);
    shown = [];
    for (const line of lastLines(lines, CHAT_LINES)) {
      const node = el('li', { class: 'chat-line' }, el('span', { class: 'from' }, line.from, ': '), el('span', { class: 'text' }, line.text));
      log.appendChild(node);
      shown.push({ node, t: line.t });
    }
  }

  function fade(serverTime: number): void {
    for (const s of shown) {
      const alpha = serverTime > 0 ? lineAlpha(serverTime - s.t, open) : 1;
      s.node.style.opacity = alpha.toFixed(2);
    }
  }

  function doOpen(): void {
    if (open || suppressed || !state.inRoom) return;
    open = true;
    input.hidden = false;
    input.value = '';
    input.focus();
    fade(state.viewModel.serverTime);
    emit();
  }

  function doClose(): void {
    if (!open) return;
    open = false;
    input.hidden = true;
    input.blur();
    emit();
  }

  /** Say what's in the box; true when it was the cheat code, which goes no further than this browser. */
  function send(): boolean {
    const text = sanitizeChat(input.value);
    input.value = '';
    if (isRevealCode(text)) {
      discoveries.reveal();
      return true;
    }
    if (text.length > 0) socket.send({ t: 'chat', text });
    return false;
  }

  input.addEventListener('keydown', (ev) => {
    ev.stopPropagation();
    if (ev.key === 'Enter') {
      ev.preventDefault();
      const code = send();
      doClose();
      if (code) revealed?.();
    } else if (ev.key === 'Escape') {
      ev.preventDefault();
      doClose();
    }
  });
  input.addEventListener('keyup', (ev) => ev.stopPropagation());
  input.addEventListener('blur', () => doClose());

  const unsubscribe = state.subscribe((vm) => {
    const visible = !suppressed && vm.room !== null;
    if (root.hidden !== !visible) root.hidden = !visible;
    if (!visible) {
      if (open) doClose();
      return;
    }
    if (vm.chat !== lastChat) {
      lastChat = vm.chat;
      renderLog(vm.chat);
      fade(vm.serverTime);
      lastFadeAt = performance.now();
    } else if (performance.now() - lastFadeAt >= FADE_STEP_MS) {
      lastFadeAt = performance.now();
      fade(vm.serverTime);
    }
  });

  root.hidden = true;
  return {
    open: doOpen,
    close: doClose,
    get isOpen(): boolean { return open; },
    onOpenChange(fn: (o: boolean) => void): () => void {
      listeners.add(fn);
      return () => { listeners.delete(fn); };
    },
    onReveal(fn: () => void): void { revealed = fn; },
    show(): void { suppressed = false; },
    hide(): void {
      suppressed = true;
      doClose();
      root.hidden = true;
    },
    dispose(): void {
      unsubscribe();
      listeners.clear();
      clear(root);
    },
  };
}
